// ─── Conversaciones fijadas del helpdesk (pin por usuario) ───────────────────
// Fijado POR USUARIO (como WhatsApp Web): el backend guarda sus propios pins y
// los devuelve en GET /conversations/pins. Mismo patrón de headers que el resto
// del helpdesk (authFetch + x-app/application) y errores con `status`/`code`
// poblados para que la UI distinga el tope (409 PIN_LIMIT_REACHED) de un fallo
// genérico.
//
// Contrato (microservice-conversations, rama feat/helpdesk-fijar-conversaciones):
//   GET   /conversations/pins      → { pins: [{ conversation_id, pinned_at }], max }
//   PATCH /conversations/:id/pin   { pinned: boolean } → { ok, pinned, pinned_at, pinned_count, max }
//   409 { code: "PIN_LIMIT_REACHED" } al superar el tope.
// El BFF reenvía /conversations/* con @All, así que no necesita cambios. Las rutas
// viven solo acá; el modelo puro normaliza el cuerpo del GET (acepta además otras
// formas por si el contrato se ajusta).

import { authFetch } from "./authFetch";
import { throwFetchApiError } from "../utils/apiErrorMessage";

// REACT_APP_API_CONNECTION ya incluye /api/v1/ al final; se recorta para poder
// armar paths completos sin duplicar el prefijo.
const BFF_BASE_URL = (process.env.REACT_APP_API_CONNECTION || "").replace(
  /\/api\/v1\/?$/,
  ""
);

/** Tope de conversaciones fijadas por usuario. */
export const PIN_LIMIT = 5;
/** Código que devuelve el backend al superar el tope. */
export const PIN_LIMIT_CODE = "PIN_LIMIT_REACHED";

// El Authorization lo pone authFetch.
function buildAuthHeaders(extra = {}) {
  return {
    "x-app": "web",
    application: "web",
    ...extra,
  };
}

/**
 * Id de conversación válido o error: nunca se arma un path con un id vacío.
 * @param {string} id
 */
function assertConversationId(id) {
  if (typeof id !== "string" || !id.trim()) {
    throw new Error("Conversación inválida");
  }
}

/**
 * ¿La conversación se puede fijar? Solo omnicanal: portal vive en otro
 * servicio y los logs de IA no son conversaciones.
 * @param {object} conv
 * @returns {boolean}
 */
export function isPinnable(conv) {
  return (
    !!conv?.id &&
    !!conv.channel &&
    conv.channel !== "portal" &&
    conv.channel !== "ai"
  );
}

/** Ruta de los fijados del usuario de la sesión. */
export function pinsPath() {
  return `${BFF_BASE_URL}/api/v1/conversations/pins`;
}

/** Ruta del pin de una conversación. */
export function pinPath(id) {
  return `${BFF_BASE_URL}/api/v1/conversations/${encodeURIComponent(id)}/pin`;
}

/**
 * Fijados del usuario de la sesión, sin normalizar (la forma aún no está
 * cerrada; la normaliza normalizePins del modelo).
 * @returns {Promise<unknown>} el cuerpo crudo
 */
export async function getPins() {
  const response = await authFetch(pinsPath(), {
    headers: buildAuthHeaders(),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json();
}

/**
 * PATCH /conversations/:id/pin con `{ pinned }` (contrato del backend: el body es
 * obligatorio y estrictamente booleano; idempotente en ambos sentidos).
 * @param {string} id
 * @param {boolean} pinned
 * @returns {Promise<string|null>} pinned_at del servidor, o null si no lo trae
 */
async function setPinned(id, pinned) {
  assertConversationId(id);
  const response = await authFetch(pinPath(id), {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(),
    },
    body: JSON.stringify({ pinned }),
  });
  if (!response.ok) await throwFetchApiError(response);
  if (response.status === 204) return null;
  const body = await response.json().catch(() => null);
  return typeof body?.pinned_at === "string" ? body.pinned_at : null;
}

/**
 * Fija una conversación. Fijar una ya fijada conserva su pinned_at original.
 * @param {string} id
 * @returns {Promise<string|null>} pinned_at del servidor, o null si no lo trae
 */
export function pinConversation(id) {
  return setPinned(id, true);
}

/**
 * Desfija una conversación (no-op si no estaba fijada).
 * @param {string} id
 * @returns {Promise<void>}
 */
export async function unpinConversation(id) {
  await setPinned(id, false);
}

/**
 * true si el error es el tope de fijados (409, con o sin code).
 * @param {unknown} err
 * @returns {boolean}
 */
export function isPinLimitError(err) {
  return err?.status === 409 || err?.code === PIN_LIMIT_CODE;
}
